import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setSort } from '../../../store/ai2';
import DataMapperPatientListTable from '../DataMapper/DataMapperPatientListTable';
import { useNavigate } from 'react-router-dom';
import { ViewPatientModal } from '../ViewPatientModal';
import { isEqual } from 'lodash';
import './PatientListTable.css';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/dist/sweetalert2.css';
import { EndlessScrollingTable } from './EndlessScrollingTable';
import { useDashboardColumns } from '../../hooks';
import { getRemoteColumns } from './utils';
import { rowClasses } from './PatientListTableFormatter';
import {
  SystemPermission,
  useLoggedInUser,
  useModal,
  useTriggerUpdate,
} from '../../../context';
import AssignToSomeoneElse from './components/AssignToSomeoneElse/AssignToSomeoneElse';
import { automedicFeaturesKey } from '../../../lib';

// copy version of src/ai2/components/Table/PatientListTable.js using endless scroll
export const PatientEndlessListTable = ({
  data,
  loading,
  timeoutError,
  currentPage,
  filterState,
  sortState,
  selectedTenancy,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { columnConfig } = useDashboardColumns();
  const { openModal } = useModal();
  const { triggerPatientListUpdate } = useTriggerUpdate();

  const { loggedInUser } = useLoggedInUser();

  const { clinics } = useSelector((state) => state.clinicianProfile);

  const canViewPatientRecord =
    !selectedTenancy?.permissionsSetup ||
    (selectedTenancy?.permissionsSetup &&
      selectedTenancy?.userPermissions &&
      !!(selectedTenancy?.userPermissions)[
        SystemPermission.VIEW_PATIENT_RECORD
      ]);

  const isAutomedicTenancy =
    selectedTenancy && automedicFeaturesKey in selectedTenancy.features;

  const getClinicianOptions = React.useCallback(
    (patient) => {
      if (!patient || !clinics) {
        return [];
      }
      const hospitals = clinics.filter(
        (clinic) => clinic.organisation_unit === 'hospital',
      );
      const patientBelongsTo = patient.clinicIds;
      if (hospitals.length === 0 || patientBelongsTo.length === 0) {
        return [];
      }

      return patient.clinicIds.flatMap((clinicId) => {
        const availableClinics = hospitals.find(
          (hospital) => hospital.clinic_id === clinicId,
        );

        // Skip if no matching clinic or clinicians
        const clinicians = availableClinics?.caremapprClinicians ?? [];

        return clinicians
          .map((clinician) => ({
            label: `${clinician.first_name} ${clinician.last_name}`,
            value: clinician.user_id,
          }))
          .filter((clinician) => clinician.value !== loggedInUser?.userId);
      });
    },
    [clinics],
  );

  const { clinicianId } = useSelector((state) => state.clinicianProfile);

  // Mapping data to table Format
  const mappedPatients = DataMapperPatientListTable(data, clinicianId);

  const triggerAssignSomeoneModal = (patientAccessToken) => {
    const patient = mappedPatients.find(
      (p) => p.access_token === patientAccessToken,
    );
    if (!patient) return;
    const clinicianOptions = getClinicianOptions(patient);
    openModal({
      content: (
        <AssignToSomeoneElse
          patient={patient}
          clinicianOptions={clinicianOptions}
        />
      ),
    });
  };

  const remoteColumns = React.useMemo(
    () =>
      getRemoteColumns(
        columnConfig,
        triggerAssignSomeoneModal,
        triggerPatientListUpdate,
        getClinicianOptions,
        isAutomedicTenancy,
      ),
    [columnConfig],
  );

  const activeSortColumn = useMemo(
    () => remoteColumns.find((column) => column.dataField === sortState.sort),
    [remoteColumns, sortState.sort],
  );

  useEffect(() => {
    if (!sortState?.sort) {
      return;
    }
    if (activeSortColumn && activeSortColumn.sort === false) {
      dispatch(setSort({ sort: 'activeInsights', direction: 'desc' }));
    }
  }, [activeSortColumn, dispatch, sortState?.sort]);

  const handleTableChange = (type, { sortField, sortOrder }) => {
    if (type === 'sort') {
      const newSortState = { sort: sortField, direction: sortOrder };
      // only update sort state if different from previous one, avoid duplicate call
      if (!isEqual(newSortState, sortState)) {
        dispatch(setSort(newSortState));
      }
    }
  };

  // View patient Modal
  const [showViewPatient, setShowViewPatient] = useState(false);
  // Patient for feedback
  const [patient, setPatient] = useState([]);

  const allowToSeeEpisodeClosedPatient =
    !!selectedTenancy?.features?.allowToSeeEpisodeClosedPatient;

  const rowEvents = {
    onClick: (e, row) => {
      if (
        !allowToSeeEpisodeClosedPatient &&
        row.lastEpisode.indexOf('Open') === -1
      ) {
        Swal.fire({
          icon: 'info',
          text: 'Patient episode is now closed',
          showConfirmButton: true,
        });
        return;
      }

      if (
        Array.isArray(row.clinicianSurveyToday) &&
        row.clinicianSurveyToday?.length === 0 &&
        selectedTenancy.questionnaires?.length !== 0 &&
        canViewPatientRecord
      ) {
        setPatient(row);
        setShowViewPatient(true);
        return;
      }

      if (row.access_token && canViewPatientRecord) {
        navigate(`/patientDetails?access_token=${row.access_token}`);
      }
    },
  };

  return (
    <>
      <EndlessScrollingTable
        loading={loading}
        timeoutError={timeoutError}
        data={mappedPatients}
        columns={remoteColumns}
        page={currentPage}
        // This Handle will get a call on every click of pagination and sort button
        rowClasses={rowClasses}
        rowEvents={rowEvents}
        sort={
          activeSortColumn && activeSortColumn.sort === false
            ? undefined
            : { dataField: sortState.sort, order: sortState.direction }
        }
        handleTableChange={handleTableChange}
      />
      {showViewPatient && (
        <div>
          <ViewPatientModal
            toChild={showViewPatient}
            sendToParent={setShowViewPatient}
            patient={patient}
          />
        </div>
      )}
    </>
  );
};
